import type { PaymentOption, PublicSession } from '../api/types';
import { explorerTxUrl } from '../chains';
import { isEmbedded, post } from '../embed-bridge';
import { t } from '../i18n';
import { formatFiat, formatInt, fromMinor, shortHash, trimDecimal } from '../utils/format';

interface Props {
  session: PublicSession;
  option?: PaymentOption;
}

export function StatusBanner({ session, option }: Props) {
  const s = session.status;
  const latest = [...session.payments].sort((a, b) => Date.parse(b.detected_at) - Date.parse(a.detected_at))[0];
  const merchant = session.merchant.name;
  const credits = session.purpose === 'credits';
  const paid = session.paid;
  // The option actually paid with wins over the one currently selected.
  const paidOption = paid?.asset ? session.payment_options.find((o) => o.asset === paid.asset) : undefined;
  const opt = paidOption ?? option;
  const symbol = latest?.symbol ?? opt?.symbol ?? '';

  let title = '';
  let hint = '';
  let icon = '';
  switch (s) {
    case 'pending':
      title = t('status.pending');
      hint = t('status.pendingHint');
      icon = '◌';
      break;
    case 'confirming': {
      title = t('status.confirming');
      const conf = paid ?? latest;
      hint = conf ? t('status.confirmingHint', { confirmations: formatInt(conf.confirmations), required: formatInt(conf.required_confirmations) }) : '';
      icon = '◔';
      break;
    }
    case 'paid':
      title = credits ? t('status.paidCreditsTitle') : t('status.paid');
      hint = credits ? t('status.paidCredits') : t('status.paidHint', { merchant });
      icon = '✓';
      break;
    case 'underpaid': {
      title = t('status.underpaid');
      let received: string;
      let remaining: string;
      if (paid) {
        received = trimDecimal(paid.amount_received);
        remaining = trimDecimal(paid.amount_remaining);
      } else {
        const decimals = opt?.decimals ?? 8;
        const got = session.payments
          .filter((p) => (!opt || p.asset === opt.asset) && p.status === 'confirmed')
          .reduce((acc, p) => acc + BigInt(p.amount_minor), 0n);
        const due = opt ? BigInt(opt.amount_minor) : 0n;
        received = trimDecimal(fromMinor(got, decimals));
        remaining = trimDecimal(fromMinor(due > got ? due - got : 0n, decimals));
      }
      hint = t('status.underpaidHint', { received, remaining, asset: symbol });
      icon = '!';
      break;
    }
    case 'overpaid':
      title = t('status.overpaid');
      hint =
        (credits ? t('status.paidCredits') + ' ' : '') +
        t('status.overpaidHint', {
          merchant,
          excess: session.balance_delta ? formatFiat(session.balance_delta.replace('-', ''), session.currency) : '',
          currency: '',
        }).replace(/\s+\.$/, '.');
      icon = '✓';
      break;
    case 'expired':
      title = t('status.expired');
      hint = t('status.expiredHint', { merchant });
      icon = '✕';
      break;
    case 'cancelled':
      title = t('status.cancelled');
      hint = t('status.cancelledHint');
      icon = '✕';
      break;
  }

  const done = s === 'paid' || s === 'overpaid';
  const confBar = paid ?? latest;
  const txUrl = latest ? latest.explorer_url || explorerTxUrl(latest.chain, latest.tx_hash) : null;

  return (
    <div class={`cv-status ${s}`} role="status" aria-live="polite" aria-label={t('a11y.statusLive')}>
      <div class={`ic${s === 'pending' || s === 'confirming' ? ' cv-pulse' : ''}`} aria-hidden="true">
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h2>{title}</h2>
        {hint && <p>{hint}</p>}
        {s === 'confirming' && confBar && (
          <div class="cv-conf-bar" aria-hidden="true">
            {Array.from({ length: Math.min(confBar.required_confirmations, 12) }).map((_, i) => (
              <span key={i} class={i < confBar.confirmations ? 'on' : ''} />
            ))}
          </div>
        )}
        {latest && txUrl && (
          <div class="cv-tx">
            <span>{t('status.txHash')}:</span>
            <a href={txUrl} target="_blank" rel="noopener noreferrer">
              <code>{shortHash(latest.tx_hash)}</code>
            </a>
          </div>
        )}
        {done && session.success_url && !isEmbedded && (
          <p>
            {t('status.redirecting', { merchant })} <a href={session.success_url}>{t('status.continue')}</a>
          </p>
        )}
        {done && isEmbedded && (
          <button
            type="button"
            class="cv-btn primary"
            style={{ marginTop: 10 }}
            onClick={() => post({ source: 'coinvello', type: 'paid', sessionId: session.id, session })}
          >
            {t('status.continue')}
          </button>
        )}
        {(s === 'expired' || s === 'cancelled') && session.cancel_url && !isEmbedded && (
          <p>
            <a href={session.cancel_url}>{t('app.backToMerchant', { merchant })}</a>
          </p>
        )}
      </div>
    </div>
  );
}
